import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";
import {
  useNavigate,
} from "react-router-dom";

import {
  db,
} from "../firebase";

import "./Withdraw.css";

/* =========================================================
   CONFIGURATION
========================================================= */

const DEFAULT_REQUIRED_BETS = 6;
const REPEAT_DEPOSIT_REQUIRED_BETS = 4;

const DEFAULT_MIN_TZS = 10000;
const DEFAULT_MAX_TZS = 5000000;

/*
  IMPORTANT:

  USDT is a dollar-denominated balance.

  1 USDT = 2660 TZS
*/
const TZS_PER_USDT = 2660;

/*
  USDT limits based on Tanzania limits.
*/
const MIN_USDT =
  DEFAULT_MIN_TZS / TZS_PER_USDT;

const MAX_USDT =
  DEFAULT_MAX_TZS / TZS_PER_USDT;

/* =========================================================
   EXCHANGE RATES

   Meaning:

   1 USDT = rate in local currency
========================================================= */

const exchangeRates = {
  TZS: 2660,
  KES: 129.5,
  UGX: 3725,
  RWF: 1415,
  BIF: 2920,
  CDF: 2895,
  ZMW: 25.7,
  BWP: 13.7,
  MGA: 4550,
  MWK: 1745,
  MZN: 64,
  SSP: 5200,
  USD: 1,
};

/* =========================================================
   COUNTRY -> CURRENCY
========================================================= */

const countryCurrencyMap = {
  Tanzania: "TZS",
  Kenya: "KES",
  Uganda: "UGX",
  Rwanda: "RWF",
  Burundi: "BIF",
  "DRC - Congo": "CDF",
  "DR Congo": "CDF",
  Zambia: "ZMW",
  Botswana: "BWP",
  Madagascar: "MGA",
  Malawi: "MWK",
  Mozambique: "MZN",
  "South Sudan": "SSP",
  Zimbabwe: "USD",
};

/* =========================================================
   CURRENCY -> COUNTRY
========================================================= */

const currencyMap = {
  TZS: "Tanzania",
  KES: "Kenya",
  UGX: "Uganda",
  RWF: "Rwanda",
  BIF: "Burundi",
  CDF: "DRC - Congo",
  ZMW: "Zambia",
  MWK: "Malawi",
  MZN: "Mozambique",
  USD: "Zimbabwe",
  SSP: "South Sudan",
  BWP: "Botswana",
  MGA: "Madagascar",
  USDT: "USDT",
};

/* =========================================================
   BALANCE FIELDS

   IMPORTANT:

   USDT is stored ONLY in usdtBalanceOther.
========================================================= */

const balanceFields = {
  TZS: "tshBalance",
  KES: "KESBalance",
  UGX: "UGXBalance",
  RWF: "RWFBalance",
  BIF: "BIFBalance",
  CDF: "CDFBalance",
  ZMW: "ZMWBalance",
  MWK: "MWKBalance",
  MZN: "MZNBalance",
  USD: "USDBalance",
  SSP: "SSPBalance",
  BWP: "BWPBalance",
  MGA: "MGABalance",

  // DOLLAR / USDT BALANCE
  USDT: "usdtBalanceOther",
};

/* =========================================================
   COUNTRY MINIMUM AMOUNTS
========================================================= */

const countryMinAmounts = {
  Kenya: 927.64,
  Uganda: 25974.02,
  Rwanda: 10526.36,
  Burundi: 21276.6,
  "DRC - Congo": 7.24,
  "DR Congo": 7.24,
  Zambia: 135.84,
  Malawi: 14468.8,
  Mozambique: 457.56,
  Zimbabwe: 7.24,
  "South Sudan": 42553.2,
  Botswana: 94.36,
  Madagascar: 29198,
};

/* =========================================================
   WITHDRAWAL SPEED / FEE
========================================================= */

const withdrawalOptions = [
  {
    id: "standard",
    label: "Standard",
    time: "24 Hours",
    fee: 5,
  },
  {
    id: "express",
    label: "Express",
    time: "30 Minutes",
    fee: 10,
  },
];

/* =========================================================
   FORMAT NUMBER
========================================================= */

const formatAmount = (value) => {
  const number = Number(value || 0);

  return number.toLocaleString(
    undefined,
    {
      maximumFractionDigits: 6,
    }
  );
};

/* =========================================================
   MASK WALLET NUMBER
========================================================= */

const maskNumber = (number = "") => {
  const value = String(number || "")
    .replace(/\s/g, "")
    .replace(/-/g, "")
    .trim();

  if (!value) {
    return "";
  }

  if (value.length <= 4) {
    return value;
  }

  return `•••• ${value.slice(-4)}`;
};

/* =========================================================
   NORMALIZE WALLET
========================================================= */

const normalizeWallet = (wallet = {}) => {
  const country = String(
    wallet?.country || ""
  ).trim();

  const currency = String(
    wallet?.currency || ""
  )
    .trim()
    .toUpperCase();

  const network1 = String(
    wallet?.network1 ||
      wallet?.network ||
      ""
  ).trim();

  const network2 = String(
    wallet?.network2 || ""
  ).trim();

  const phoneNumber1 = String(
    wallet?.phoneNumber1 ||
      wallet?.number ||
      ""
  ).trim();

  const phoneNumber2 = String(
    wallet?.phoneNumber2 || ""
  ).trim();

  return {
    ...wallet,

    country,

    code: String(
      wallet?.code || ""
    ).trim(),

    currency,

    network: network1,
    network1,
    network2,

    number: phoneNumber1,
    phoneNumber1,
    phoneNumber2,

    type: String(
      wallet?.type || ""
    ).trim(),

    walletSecured:
      wallet?.walletSecured === true,

    primaryPhoneSecured:
      wallet?.primaryPhoneSecured === true,

    primaryNetworkSelected:
      wallet?.primaryNetworkSelected === true,

    secondPhoneAdded:
      wallet?.secondPhoneAdded === true,

    secondNetworkSelected:
      wallet?.secondNetworkSelected === true,
  };
};

/* =========================================================
   WALLET READY
========================================================= */

const isWalletReady = (wallet) => {
  if (!wallet) {
    return false;
  }

  const number = String(
    wallet.number ||
      wallet.phoneNumber1 ||
      ""
  ).trim();

  const network = String(
    wallet.network ||
      wallet.network1 ||
      ""
  ).trim();

  const currency = String(
    wallet.currency || ""
  )
    .trim()
    .toUpperCase();

  /*
    USDT is a balance denomination.

    The actual payment wallet can still be a
    supported mobile-money wallet.
  */

  if (!number || !network) {
    return false;
  }

  /*
    For normal local wallets currency is required.
  */

  if (
    !currency &&
    wallet.type !== "tanzania-phone-wallet"
  ) {
    return false;
  }

  /*
    Tanzania wallet
  */

  if (
    wallet.type ===
    "tanzania-phone-wallet"
  ) {
    const primaryPhoneReady =
      wallet.primaryPhoneSecured === true ||
      Boolean(wallet.phoneNumber1);

    const primaryNetworkReady =
      wallet.primaryNetworkSelected === true ||
      Boolean(wallet.network1);

    const primarySecured =
      wallet.walletSecured === true ||
      (
        primaryPhoneReady &&
        primaryNetworkReady
      );

    return primarySecured;
  }

  /*
    Other country wallet
  */

  return (
    wallet.walletSecured === true
  );
};

/* =========================================================
   GET RATE
========================================================= */

const getExchangeRate = (
  currency,
  wallet = null
) => {
  const normalizedCurrency =
    String(currency || "")
      .trim()
      .toUpperCase();

  /*
    When withdrawal currency itself is USDT,
    determine local payout rate from wallet country.
  */

  if (
    normalizedCurrency === "USDT"
  ) {
    const walletCountry = String(
      wallet?.country || ""
    ).trim();

    const walletCurrency =
      String(
        wallet?.currency || ""
      )
        .trim()
        .toUpperCase();

    if (
      walletCurrency &&
      exchangeRates[walletCurrency]
    ) {
      return Number(
        exchangeRates[walletCurrency]
      );
    }

    if (
      walletCountry &&
      countryCurrencyMap[walletCountry]
    ) {
      const currency =
        countryCurrencyMap[
          walletCountry
        ];

      return Number(
        exchangeRates[currency] ||
          TZS_PER_USDT
      );
    }

    return TZS_PER_USDT;
  }

  return Number(
    exchangeRates[
      normalizedCurrency
    ] || 1
  );
};

/* =========================================================
   WITHDRAW COMPONENT
========================================================= */

const WITHDRAW_TRANSLATIONS = {
  "en": {},
  "sw": {
    "Notice": "Taarifa",
    "Withdraw Channel mobile_money": "Njia ya kutoa: pesa kwa simu",
    "Usdt\\Crypto_Management": "USDT / Usimamizi wa Crypto",
    "Plan Progress": "Maendeleo ya Mpango",
    "Balance Currency": "Sarafu ya Salio",
    "USDT Rate": "Kiwango cha USDT",
    "USDT Balance": "Salio la USDT",
    "Free Balance": "Salio Huru",
    "Payment Channel": "Njia ya Malipo",
    "Loading wallet...": "Inapakia pochi...",
    "Select Wallet": "Chagua Pochi",
    "No wallet found for": "Hakuna pochi iliyopatikana kwa",
    "Add Wallet": "Ongeza Pochi",
    "Withdrawal Speed": "Kasi ya Kutoa",
    "Select Withdrawal Time": "Chagua Muda wa Kutoa",
    "Please select withdrawal time": "Tafadhali chagua muda wa kutoa",
    "Please Enter The Amount": "Tafadhali Weka Kiasi",
    "Withdrawal Fee": "Ada ya Kutoa",
    "Fee Amount": "Kiasi cha Ada",
    "You Receive": "Unapokea",
    "TZS Equivalent": "Sawa na TZS",
    "Amount Range": "Kiwango cha Kiasi",
    "SUBMIT": "WASILISHA",
    "Standard": "Kawaida",
    "Express": "Haraka",
    "24 Hours": "Saa 24",
    "30 Minutes": "Dakika 30",
    "Fee": "Ada",
    "Enter amount Min": "Weka kiasi cha chini",
    "withdrawal fee": "ada ya kutoa",
    "Select withdrawal time to view processing fee": "Chagua muda wa kutoa ili kuona ada ya uchakataji",
    "Actual transaction amount may\n          still be subject to bank charges.": "Kiasi halisi cha muamala kinaweza pia kutozwa ada za benki."
  },
  "fr": {
    "Notice": "Avis",
    "Withdraw Channel mobile_money": "Canal de retrait : argent mobile",
    "Usdt\\Crypto_Management": "USDT / Gestion crypto",
    "Plan Progress": "Progression du plan",
    "Balance Currency": "Devise du solde",
    "USDT Rate": "Taux USDT",
    "USDT Balance": "Solde USDT",
    "Free Balance": "Solde disponible",
    "Payment Channel": "Canal de paiement",
    "Loading wallet...": "Chargement du portefeuille...",
    "Select Wallet": "Sélectionner le portefeuille",
    "Add Wallet": "Ajouter un portefeuille",
    "Withdrawal Speed": "Délai de retrait",
    "Select Withdrawal Time": "Choisir le délai de retrait",
    "Please select withdrawal time": "Veuillez choisir le délai de retrait",
    "Please Enter The Amount": "Veuillez saisir le montant",
    "Withdrawal Fee": "Frais de retrait",
    "Fee Amount": "Montant des frais",
    "You Receive": "Vous recevez",
    "TZS Equivalent": "Équivalent en TZS",
    "Amount Range": "Plage de montant",
    "SUBMIT": "ENVOYER",
    "Standard": "Standard",
    "Express": "Express",
    "24 Hours": "24 heures",
    "30 Minutes": "30 minutes",
    "Fee": "Frais",
    "withdrawal fee": "frais de retrait",
    "Select withdrawal time to view processing fee": "Choisissez le délai de retrait pour voir les frais",
    "Actual transaction amount may\n          still be subject to bank charges.": "Le montant réel de la transaction peut également être soumis à des frais bancaires."
  },
  "es": {
    "Notice": "Aviso",
    "Withdraw Channel mobile_money": "Canal de retiro: dinero móvil",
    "Usdt\\Crypto_Management": "USDT / Gestión de cripto",
    "Plan Progress": "Progreso del plan",
    "Balance Currency": "Moneda del saldo",
    "USDT Rate": "Tasa USDT",
    "USDT Balance": "Saldo USDT",
    "Free Balance": "Saldo disponible",
    "Payment Channel": "Canal de pago",
    "Loading wallet...": "Cargando billetera...",
    "Select Wallet": "Seleccionar billetera",
    "Add Wallet": "Añadir billetera",
    "Withdrawal Speed": "Velocidad de retiro",
    "Select Withdrawal Time": "Seleccionar tiempo de retiro",
    "Please select withdrawal time": "Selecciona el tiempo de retiro",
    "Please Enter The Amount": "Introduce el importe",
    "Withdrawal Fee": "Tarifa de retiro",
    "Fee Amount": "Importe de la tarifa",
    "You Receive": "Recibes",
    "TZS Equivalent": "Equivalente en TZS",
    "Amount Range": "Rango de importe",
    "SUBMIT": "ENVIAR",
    "Standard": "Estándar",
    "Express": "Exprés",
    "24 Hours": "24 horas",
    "30 Minutes": "30 minutos",
    "Fee": "Tarifa",
    "withdrawal fee": "tarifa de retiro",
    "Select withdrawal time to view processing fee": "Selecciona el tiempo de retiro para ver la tarifa",
    "Actual transaction amount may\n          still be subject to bank charges.": "El importe real de la transacción también puede estar sujeto a cargos bancarios."
  },
  "pt": {
    "Notice": "Aviso",
    "Withdraw Channel mobile_money": "Canal de saque: dinheiro móvel",
    "Usdt\\Crypto_Management": "USDT / Gestão de cripto",
    "Plan Progress": "Progresso do plano",
    "Balance Currency": "Moeda do saldo",
    "USDT Rate": "Taxa USDT",
    "USDT Balance": "Saldo USDT",
    "Free Balance": "Saldo disponível",
    "Payment Channel": "Canal de pagamento",
    "Loading wallet...": "A carregar carteira...",
    "Select Wallet": "Selecionar carteira",
    "Add Wallet": "Adicionar carteira",
    "Withdrawal Speed": "Velocidade de levantamento",
    "Select Withdrawal Time": "Selecionar tempo de levantamento",
    "Please select withdrawal time": "Selecione o tempo de levantamento",
    "Please Enter The Amount": "Introduza o valor",
    "Withdrawal Fee": "Taxa de levantamento",
    "Fee Amount": "Valor da taxa",
    "You Receive": "Recebe",
    "TZS Equivalent": "Equivalente em TZS",
    "Amount Range": "Intervalo do valor",
    "SUBMIT": "ENVIAR",
    "Standard": "Padrão",
    "Express": "Expresso",
    "24 Hours": "24 horas",
    "30 Minutes": "30 minutos",
    "Fee": "Taxa",
    "withdrawal fee": "taxa de levantamento",
    "Select withdrawal time to view processing fee": "Selecione o tempo de levantamento para ver a taxa",
    "Actual transaction amount may\n          still be subject to bank charges.": "O valor real da transação também pode estar sujeito a encargos bancários."
  },
  "de": {
    "Notice": "Hinweis",
    "Withdraw Channel mobile_money": "Auszahlungskanal: mobiles Geld",
    "Usdt\\Crypto_Management": "USDT / Krypto-Verwaltung",
    "Plan Progress": "Planfortschritt",
    "Balance Currency": "Kontowährung",
    "USDT Rate": "USDT-Kurs",
    "USDT Balance": "USDT-Guthaben",
    "Free Balance": "Verfügbares Guthaben",
    "Payment Channel": "Zahlungskanal",
    "Loading wallet...": "Wallet wird geladen...",
    "Select Wallet": "Wallet auswählen",
    "Add Wallet": "Wallet hinzufügen",
    "Withdrawal Speed": "Auszahlungsgeschwindigkeit",
    "Select Withdrawal Time": "Auszahlungszeit wählen",
    "Please select withdrawal time": "Bitte Auszahlungszeit wählen",
    "Please Enter The Amount": "Bitte Betrag eingeben",
    "Withdrawal Fee": "Auszahlungsgebühr",
    "Fee Amount": "Gebührenbetrag",
    "You Receive": "Sie erhalten",
    "TZS Equivalent": "Entspricht TZS",
    "Amount Range": "Betragsbereich",
    "SUBMIT": "ABSENDEN",
    "Standard": "Standard",
    "Express": "Express",
    "24 Hours": "24 Stunden",
    "30 Minutes": "30 Minuten",
    "Fee": "Gebühr",
    "withdrawal fee": "Auszahlungsgebühr",
    "Select withdrawal time to view processing fee": "Wählen Sie die Auszahlungszeit, um die Gebühr zu sehen",
    "Actual transaction amount may\n          still be subject to bank charges.": "Der tatsächliche Transaktionsbetrag kann zusätzlich Bankgebühren unterliegen."
  },
  "it": {
    "Notice": "Avviso",
    "Plan Progress": "Avanzamento del piano",
    "Balance Currency": "Valuta del saldo",
    "USDT Rate": "Tasso USDT",
    "USDT Balance": "Saldo USDT",
    "Free Balance": "Saldo disponibile",
    "Payment Channel": "Canale di pagamento",
    "Loading wallet...": "Caricamento portafoglio...",
    "Select Wallet": "Seleziona portafoglio",
    "Add Wallet": "Aggiungi portafoglio",
    "Withdrawal Speed": "Velocità di prelievo",
    "Select Withdrawal Time": "Seleziona il tempo di prelievo",
    "Please select withdrawal time": "Seleziona il tempo di prelievo",
    "Please Enter The Amount": "Inserisci l'importo",
    "Withdrawal Fee": "Commissione di prelievo",
    "Fee Amount": "Importo commissione",
    "You Receive": "Ricevi",
    "TZS Equivalent": "Equivalente in TZS",
    "Amount Range": "Intervallo importo",
    "SUBMIT": "INVIA",
    "Standard": "Standard",
    "Express": "Rapido",
    "24 Hours": "24 ore",
    "30 Minutes": "30 minuti",
    "Fee": "Commissione"
  },
  "nl": {
    "Notice": "Melding",
    "Plan Progress": "Voortgang van het plan",
    "Balance Currency": "Saldo-valuta",
    "USDT Rate": "USDT-koers",
    "USDT Balance": "USDT-saldo",
    "Free Balance": "Beschikbaar saldo",
    "Payment Channel": "Betaalkanaal",
    "Loading wallet...": "Portemonnee laden...",
    "Select Wallet": "Selecteer portemonnee",
    "Add Wallet": "Portemonnee toevoegen",
    "Withdrawal Speed": "Opnamesnelheid",
    "Select Withdrawal Time": "Selecteer opnametijd",
    "Please select withdrawal time": "Selecteer de opnametijd",
    "Please Enter The Amount": "Voer het bedrag in",
    "Withdrawal Fee": "Opnamekosten",
    "Fee Amount": "Kostenbedrag",
    "You Receive": "Je ontvangt",
    "TZS Equivalent": "Equivalent in TZS",
    "Amount Range": "Bedragbereik",
    "SUBMIT": "VERZENDEN",
    "Standard": "Standaard",
    "Express": "Snel",
    "24 Hours": "24 uur",
    "30 Minutes": "30 minuten",
    "Fee": "Kosten"
  },
  "ar": {
    "Notice": "تنبيه",
    "Plan Progress": "تقدم الخطة",
    "Balance Currency": "عملة الرصيد",
    "USDT Rate": "سعر USDT",
    "USDT Balance": "رصيد USDT",
    "Free Balance": "الرصيد المتاح",
    "Payment Channel": "قناة الدفع",
    "Loading wallet...": "جارٍ تحميل المحفظة...",
    "Select Wallet": "اختر المحفظة",
    "Add Wallet": "إضافة محفظة",
    "Withdrawal Speed": "سرعة السحب",
    "Select Withdrawal Time": "اختر وقت السحب",
    "Please select withdrawal time": "يرجى اختيار وقت السحب",
    "Please Enter The Amount": "يرجى إدخال المبلغ",
    "Withdrawal Fee": "رسوم السحب",
    "Fee Amount": "مبلغ الرسوم",
    "You Receive": "ستستلم",
    "TZS Equivalent": "ما يعادل TZS",
    "Amount Range": "نطاق المبلغ",
    "SUBMIT": "إرسال",
    "Standard": "عادي",
    "Express": "سريع",
    "24 Hours": "24 ساعة",
    "30 Minutes": "30 دقيقة",
    "Fee": "رسوم",
    "withdrawal fee": "رسوم السحب",
    "Select withdrawal time to view processing fee": "اختر وقت السحب لعرض رسوم المعالجة",
    "Actual transaction amount may\n          still be subject to bank charges.": "قد يخضع مبلغ المعاملة الفعلي أيضًا لرسوم مصرفية."
  },
  "zh": {
    "Notice": "提示",
    "Plan Progress": "计划进度",
    "Balance Currency": "余额币种",
    "USDT Rate": "USDT 汇率",
    "USDT Balance": "USDT 余额",
    "Free Balance": "可用余额",
    "Payment Channel": "付款渠道",
    "Loading wallet...": "正在加载钱包…",
    "Select Wallet": "选择钱包",
    "Add Wallet": "添加钱包",
    "Withdrawal Speed": "提现速度",
    "Select Withdrawal Time": "选择提现时间",
    "Please select withdrawal time": "请选择提现时间",
    "Please Enter The Amount": "请输入金额",
    "Withdrawal Fee": "提现手续费",
    "Fee Amount": "手续费金额",
    "You Receive": "您将收到",
    "TZS Equivalent": "折合 TZS",
    "Amount Range": "金额范围",
    "SUBMIT": "提交",
    "Standard": "标准",
    "Express": "快速",
    "24 Hours": "24 小时",
    "30 Minutes": "30 分钟",
    "Fee": "费用",
    "withdrawal fee": "提现手续费",
    "Select withdrawal time to view processing fee": "选择提现时间以查看处理费用",
    "Actual transaction amount may\n          still be subject to bank charges.": "实际交易金额可能还需支付银行费用。"
  },
  "hi": {
    "Notice": "सूचना",
    "Plan Progress": "योजना की प्रगति",
    "Balance Currency": "बैलेंस मुद्रा",
    "USDT Rate": "USDT दर",
    "USDT Balance": "USDT बैलेंस",
    "Free Balance": "उपलब्ध बैलेंस",
    "Payment Channel": "भुगतान माध्यम",
    "Loading wallet...": "वॉलेट लोड हो रहा है...",
    "Select Wallet": "वॉलेट चुनें",
    "Add Wallet": "वॉलेट जोड़ें",
    "Withdrawal Speed": "निकासी गति",
    "Select Withdrawal Time": "निकासी का समय चुनें",
    "Please select withdrawal time": "कृपया निकासी का समय चुनें",
    "Please Enter The Amount": "कृपया राशि दर्ज करें",
    "Withdrawal Fee": "निकासी शुल्क",
    "Fee Amount": "शुल्क राशि",
    "You Receive": "आपको मिलेगा",
    "TZS Equivalent": "TZS के बराबर",
    "Amount Range": "राशि सीमा",
    "SUBMIT": "जमा करें",
    "Standard": "सामान्य",
    "Express": "एक्सप्रेस",
    "24 Hours": "24 घंटे",
    "30 Minutes": "30 मिनट",
    "Fee": "शुल्क",
    "withdrawal fee": "निकासी शुल्क",
    "Select withdrawal time to view processing fee": "प्रोसेसिंग शुल्क देखने के लिए निकासी समय चुनें",
    "Actual transaction amount may\n          still be subject to bank charges.": "वास्तविक लेन-देन राशि पर बैंक शुल्क भी लग सकते हैं।"
  },
  "ru": {
    "Notice": "Уведомление",
    "Plan Progress": "Прогресс плана",
    "Balance Currency": "Валюта баланса",
    "USDT Rate": "Курс USDT",
    "USDT Balance": "Баланс USDT",
    "Free Balance": "Доступный баланс",
    "Payment Channel": "Способ оплаты",
    "Loading wallet...": "Загрузка кошелька...",
    "Select Wallet": "Выбрать кошелёк",
    "Add Wallet": "Добавить кошелёк",
    "Withdrawal Speed": "Скорость вывода",
    "Select Withdrawal Time": "Выберите время вывода",
    "Please select withdrawal time": "Выберите время вывода",
    "Please Enter The Amount": "Введите сумму",
    "Withdrawal Fee": "Комиссия за вывод",
    "Fee Amount": "Сумма комиссии",
    "You Receive": "Вы получите",
    "TZS Equivalent": "Эквивалент в TZS",
    "Amount Range": "Диапазон суммы",
    "SUBMIT": "ОТПРАВИТЬ",
    "Standard": "Стандартный",
    "Express": "Экспресс",
    "24 Hours": "24 часа",
    "30 Minutes": "30 минут",
    "Fee": "Комиссия"
  },
  "bn": {
    "Notice": "বিজ্ঞপ্তি",
    "Plan Progress": "পরিকল্পনার অগ্রগতি",
    "Balance Currency": "ব্যালেন্সের মুদ্রা",
    "USDT Rate": "USDT বিনিময় হার",
    "USDT Balance": "USDT ব্যালেন্স",
    "Free Balance": "ব্যবহারযোগ্য ব্যালেন্স",
    "Payment Channel": "পেমেন্ট মাধ্যম",
    "Loading wallet...": "ওয়ালেট লোড হচ্ছে...",
    "Select Wallet": "ওয়ালেট নির্বাচন করুন",
    "Add Wallet": "ওয়ালেট যোগ করুন",
    "Withdrawal Speed": "উত্তোলনের গতি",
    "Select Withdrawal Time": "উত্তোলনের সময় নির্বাচন করুন",
    "Please select withdrawal time": "অনুগ্রহ করে উত্তোলনের সময় নির্বাচন করুন",
    "Please Enter The Amount": "অনুগ্রহ করে পরিমাণ লিখুন",
    "Withdrawal Fee": "উত্তোলন ফি",
    "Fee Amount": "ফির পরিমাণ",
    "You Receive": "আপনি পাবেন",
    "TZS Equivalent": "TZS সমতুল্য",
    "Amount Range": "পরিমাণের সীমা",
    "SUBMIT": "জমা দিন",
    "Standard": "সাধারণ",
    "Express": "দ্রুত",
    "24 Hours": "২৪ ঘণ্টা",
    "30 Minutes": "৩০ মিনিট",
    "Fee": "ফি"
  },
  "tr": {
    "Notice": "Bildirim",
    "Plan Progress": "Plan İlerlemesi",
    "Balance Currency": "Bakiye Para Birimi",
    "USDT Rate": "USDT Kuru",
    "USDT Balance": "USDT Bakiyesi",
    "Free Balance": "Kullanılabilir Bakiye",
    "Payment Channel": "Ödeme Kanalı",
    "Loading wallet...": "Cüzdan yükleniyor...",
    "Select Wallet": "Cüzdan Seç",
    "Add Wallet": "Cüzdan Ekle",
    "Withdrawal Speed": "Para Çekme Hızı",
    "Select Withdrawal Time": "Para Çekme Süresini Seç",
    "Please select withdrawal time": "Lütfen para çekme süresini seçin",
    "Please Enter The Amount": "Lütfen tutarı girin",
    "Withdrawal Fee": "Para Çekme Ücreti",
    "Fee Amount": "Ücret Tutarı",
    "You Receive": "Alacağınız",
    "TZS Equivalent": "TZS Karşılığı",
    "Amount Range": "Tutar Aralığı",
    "SUBMIT": "GÖNDER",
    "Standard": "Standart",
    "Express": "Hızlı",
    "24 Hours": "24 Saat",
    "30 Minutes": "30 Dakika",
    "Fee": "Ücret"
  },
  "ja": {
    "Notice": "お知らせ",
    "Plan Progress": "プランの進捗",
    "Balance Currency": "残高通貨",
    "USDT Rate": "USDTレート",
    "USDT Balance": "USDT残高",
    "Free Balance": "利用可能残高",
    "Payment Channel": "支払い方法",
    "Loading wallet...": "ウォレットを読み込み中...",
    "Select Wallet": "ウォレットを選択",
    "Add Wallet": "ウォレットを追加",
    "Withdrawal Speed": "出金速度",
    "Select Withdrawal Time": "出金時間を選択",
    "Please select withdrawal time": "出金時間を選択してください",
    "Please Enter The Amount": "金額を入力してください",
    "Withdrawal Fee": "出金手数料",
    "Fee Amount": "手数料額",
    "You Receive": "受取額",
    "TZS Equivalent": "TZS換算額",
    "Amount Range": "金額範囲",
    "SUBMIT": "送信",
    "Standard": "通常",
    "Express": "高速",
    "24 Hours": "24時間",
    "30 Minutes": "30分",
    "Fee": "手数料"
  },
  "ko": {
    "Notice": "알림",
    "Plan Progress": "플랜 진행 상황",
    "Balance Currency": "잔액 통화",
    "USDT Rate": "USDT 환율",
    "USDT Balance": "USDT 잔액",
    "Free Balance": "사용 가능 잔액",
    "Payment Channel": "결제 채널",
    "Loading wallet...": "지갑을 불러오는 중...",
    "Select Wallet": "지갑 선택",
    "Add Wallet": "지갑 추가",
    "Withdrawal Speed": "출금 속도",
    "Select Withdrawal Time": "출금 시간 선택",
    "Please select withdrawal time": "출금 시간을 선택하세요",
    "Please Enter The Amount": "금액을 입력하세요",
    "Withdrawal Fee": "출금 수수료",
    "Fee Amount": "수수료 금액",
    "You Receive": "받을 금액",
    "TZS Equivalent": "TZS 환산액",
    "Amount Range": "금액 범위",
    "SUBMIT": "제출",
    "Standard": "일반",
    "Express": "빠른 출금",
    "24 Hours": "24시간",
    "30 Minutes": "30분",
    "Fee": "수수료"
  },
  "vi": {
    "Notice": "Thông báo",
    "Plan Progress": "Tiến độ kế hoạch",
    "Balance Currency": "Đơn vị tiền tệ số dư",
    "USDT Rate": "Tỷ giá USDT",
    "USDT Balance": "Số dư USDT",
    "Free Balance": "Số dư khả dụng",
    "Payment Channel": "Kênh thanh toán",
    "Loading wallet...": "Đang tải ví...",
    "Select Wallet": "Chọn ví",
    "Add Wallet": "Thêm ví",
    "Withdrawal Speed": "Tốc độ rút tiền",
    "Select Withdrawal Time": "Chọn thời gian rút",
    "Please select withdrawal time": "Vui lòng chọn thời gian rút",
    "Please Enter The Amount": "Vui lòng nhập số tiền",
    "Withdrawal Fee": "Phí rút tiền",
    "Fee Amount": "Số phí",
    "You Receive": "Bạn nhận được",
    "TZS Equivalent": "Tương đương TZS",
    "Amount Range": "Khoảng số tiền",
    "SUBMIT": "GỬI",
    "Standard": "Tiêu chuẩn",
    "Express": "Nhanh",
    "24 Hours": "24 giờ",
    "30 Minutes": "30 phút",
    "Fee": "Phí"
  },
  "th": {
    "Notice": "แจ้งเตือน",
    "Plan Progress": "ความคืบหน้าของแผน",
    "Balance Currency": "สกุลเงินยอดคงเหลือ",
    "USDT Rate": "อัตรา USDT",
    "USDT Balance": "ยอด USDT",
    "Free Balance": "ยอดที่ใช้ได้",
    "Payment Channel": "ช่องทางชำระเงิน",
    "Loading wallet...": "กำลังโหลดกระเป๋าเงิน...",
    "Select Wallet": "เลือกกระเป๋าเงิน",
    "Add Wallet": "เพิ่มกระเป๋าเงิน",
    "Withdrawal Speed": "ความเร็วในการถอน",
    "Select Withdrawal Time": "เลือกเวลาถอน",
    "Please select withdrawal time": "โปรดเลือกเวลาถอน",
    "Please Enter The Amount": "โปรดป้อนจำนวนเงิน",
    "Withdrawal Fee": "ค่าธรรมเนียมถอน",
    "Fee Amount": "จำนวนค่าธรรมเนียม",
    "You Receive": "คุณจะได้รับ",
    "TZS Equivalent": "เทียบเท่า TZS",
    "Amount Range": "ช่วงจำนวนเงิน",
    "SUBMIT": "ส่ง",
    "Standard": "มาตรฐาน",
    "Express": "ด่วน",
    "24 Hours": "24 ชั่วโมง",
    "30 Minutes": "30 นาที",
    "Fee": "ค่าธรรมเนียม"
  },
  "id": {
    "Notice": "Pemberitahuan",
    "Plan Progress": "Progres Rencana",
    "Balance Currency": "Mata Uang Saldo",
    "USDT Rate": "Kurs USDT",
    "USDT Balance": "Saldo USDT",
    "Free Balance": "Saldo Tersedia",
    "Payment Channel": "Saluran Pembayaran",
    "Loading wallet...": "Memuat dompet...",
    "Select Wallet": "Pilih Dompet",
    "Add Wallet": "Tambah Dompet",
    "Withdrawal Speed": "Kecepatan Penarikan",
    "Select Withdrawal Time": "Pilih Waktu Penarikan",
    "Please select withdrawal time": "Silakan pilih waktu penarikan",
    "Please Enter The Amount": "Masukkan jumlah",
    "Withdrawal Fee": "Biaya Penarikan",
    "Fee Amount": "Jumlah Biaya",
    "You Receive": "Anda Menerima",
    "TZS Equivalent": "Setara TZS",
    "Amount Range": "Rentang Jumlah",
    "SUBMIT": "KIRIM",
    "Standard": "Standar",
    "Express": "Ekspres",
    "24 Hours": "24 Jam",
    "30 Minutes": "30 Menit",
    "Fee": "Biaya"
  },
  "ms": {
    "Notice": "Makluman",
    "Plan Progress": "Kemajuan Pelan",
    "Balance Currency": "Mata Wang Baki",
    "USDT Rate": "Kadar USDT",
    "USDT Balance": "Baki USDT",
    "Free Balance": "Baki Tersedia",
    "Payment Channel": "Saluran Pembayaran",
    "Loading wallet...": "Memuatkan dompet...",
    "Select Wallet": "Pilih Dompet",
    "Add Wallet": "Tambah Dompet",
    "Withdrawal Speed": "Kelajuan Pengeluaran",
    "Select Withdrawal Time": "Pilih Masa Pengeluaran",
    "Please select withdrawal time": "Sila pilih masa pengeluaran",
    "Please Enter The Amount": "Sila masukkan jumlah",
    "Withdrawal Fee": "Yuran Pengeluaran",
    "Fee Amount": "Jumlah Yuran",
    "You Receive": "Anda Terima",
    "TZS Equivalent": "Setara TZS",
    "Amount Range": "Julat Jumlah",
    "SUBMIT": "HANTAR",
    "Standard": "Standard",
    "Express": "Ekspres",
    "24 Hours": "24 Jam",
    "30 Minutes": "30 Minit",
    "Fee": "Yuran"
  },
  "ur": {
    "Notice": "اطلاع",
    "Plan Progress": "منصوبے کی پیش رفت",
    "Balance Currency": "بیلنس کی کرنسی",
    "USDT Rate": "USDT شرح",
    "USDT Balance": "USDT بیلنس",
    "Free Balance": "دستیاب بیلنس",
    "Payment Channel": "ادائیگی کا طریقہ",
    "Loading wallet...": "والٹ لوڈ ہو رہا ہے...",
    "Select Wallet": "والٹ منتخب کریں",
    "Add Wallet": "والٹ شامل کریں",
    "Withdrawal Speed": "رقم نکالنے کی رفتار",
    "Select Withdrawal Time": "رقم نکالنے کا وقت منتخب کریں",
    "Please select withdrawal time": "براہ کرم رقم نکالنے کا وقت منتخب کریں",
    "Please Enter The Amount": "براہ کرم رقم درج کریں",
    "Withdrawal Fee": "رقم نکالنے کی فیس",
    "Fee Amount": "فیس کی رقم",
    "You Receive": "آپ کو ملے گا",
    "TZS Equivalent": "TZS کے مساوی",
    "Amount Range": "رقم کی حد",
    "SUBMIT": "جمع کریں",
    "Standard": "معیاری",
    "Express": "فوری",
    "24 Hours": "24 گھنٹے",
    "30 Minutes": "30 منٹ",
    "Fee": "فیس"
  },
  "fa": {
    "Notice": "اعلان",
    "Plan Progress": "پیشرفت برنامه",
    "Balance Currency": "واحد پول موجودی",
    "USDT Rate": "نرخ USDT",
    "USDT Balance": "موجودی USDT",
    "Free Balance": "موجودی قابل برداشت",
    "Payment Channel": "روش پرداخت",
    "Loading wallet...": "در حال بارگذاری کیف پول...",
    "Select Wallet": "انتخاب کیف پول",
    "Add Wallet": "افزودن کیف پول",
    "Withdrawal Speed": "سرعت برداشت",
    "Select Withdrawal Time": "انتخاب زمان برداشت",
    "Please select withdrawal time": "لطفاً زمان برداشت را انتخاب کنید",
    "Please Enter The Amount": "لطفاً مبلغ را وارد کنید",
    "Withdrawal Fee": "کارمزد برداشت",
    "Fee Amount": "مبلغ کارمزد",
    "You Receive": "دریافت می‌کنید",
    "TZS Equivalent": "معادل TZS",
    "Amount Range": "محدوده مبلغ",
    "SUBMIT": "ثبت",
    "Standard": "استاندارد",
    "Express": "سریع",
    "24 Hours": "۲۴ ساعت",
    "30 Minutes": "۳۰ دقیقه",
    "Fee": "کارمزد"
  },
  "ha": {},
  "yo": {},
  "zu": {},
  "am": {},
  "so": {},
  "rw": {},
  "lg": {},
  "af": {}
};

const WITHDRAW_LANGUAGES = ["en","fr","sw","pt","es","ar","zh","hi","bn","ru","de","it","nl","tr","ja","ko","vi","th","id","ms","ur","fa","ha","yo","zu","am","so","rw","lg","af"];

function getWithdrawLanguage() {
  const saved = (typeof window !== "undefined" && window.localStorage.getItem("language")) || "en";
  return WITHDRAW_LANGUAGES.includes(saved) ? saved : "en";
}

export default function Withdraw() {

  const [language, setLanguage] = useState(getWithdrawLanguage);

  useEffect(() => {
    const updateLanguage = () => setLanguage(getWithdrawLanguage());
    window.addEventListener("languageChanged", updateLanguage);
    window.addEventListener("storage", updateLanguage);
    return () => {
      window.removeEventListener("languageChanged", updateLanguage);
      window.removeEventListener("storage", updateLanguage);
    };
  }, []);

  const t = (key) => (WITHDRAW_TRANSLATIONS[language] && WITHDRAW_TRANSLATIONS[language][key]) || key;

// Kagua kiasi kinachoweza kutolewa baada ya kuhesabu margin
const checkWithdrawableBalance = async (username) => {
  const userQuery = query(
    collection(db, "users"),
    where("username", "==", username),
    limit(1)
  );

  const userSnapshot = await getDocs(userQuery);

  if (userSnapshot.empty) {
    throw new Error(t("User account was not found."));
  }

  const userData = userSnapshot.docs[0].data();
  const balance = Number(userData.usdtBalanceOther);

  if (!Number.isFinite(balance) || balance < 0) {
    throw new Error(t("Account balance is invalid."));
  }

  const forexQuery = query(
    collection(db, "Forex"),
    where("username", "==", username)
  );

  const forexSnapshot = await getDocs(forexQuery);

  const usedMargin = forexSnapshot.docs.reduce((total, item) => {
    const order = item.data();
    const status = String(order.status || "").toLowerCase();

    const closed =
      order.closed === true ||
      [
        "closed",
        "cancelled",
        "canceled",
        "rejected",
        "expired",
      ].includes(status);

    const active =
      !closed &&
      (
        status === "open" ||
        status === "pending" ||
        order.isPending === true
      );

    if (!active) return total;

    const margin = Number(
      order.marginRequired ?? order.margin ?? 0
    );

    if (!Number.isFinite(margin) || margin < 0) {
      throw new Error(t("An existing Forex order has invalid margin."));
    }

    return total + margin;
  }, 0);

  return {
    balance,
    usedMargin,
    freeBalance: Math.max(0, balance - usedMargin),
  };
};

  const navigate = useNavigate();

  /* =======================================================
     USERNAME
  ======================================================= */

  const username =
    localStorage.getItem(
      "username"
    ) || "";

  /* =======================================================
     STATES
  ======================================================= */

  const [
    userWallets,
    setUserWallets,
  ] = useState([]);

  const [
    selectedWallet,
    setSelectedWallet,
  ] = useState("");

  const [
    amount,
    setAmount,
  ] = useState("");

  const [
    balances,
    setBalances,
  ] = useState({});
  const [freeBalance, setFreeBalance] = useState(0);
  const [marginLoading, setMarginLoading] = useState(false);
  const [
    selectedCurrency,
    setSelectedCurrency,
  ] = useState("TZS");

  const [
    walletLoading,
    setWalletLoading,
  ] = useState(true);

  const [
    securityLoading,
    setSecurityLoading,
  ] = useState(true);

  const [
    totalBets,
    setTotalBets,
  ] = useState(0);

  const [requiredBets, setRequiredBets] = useState(DEFAULT_REQUIRED_BETS);

  const [
    withdrawalType,
    setWithdrawalType,
  ] = useState("");

  const [
    withdrawalSpeedError,
    setWithdrawalSpeedError,
  ] = useState(false);

  const [
    amountError,
    setAmountError,
  ] = useState(false);

  const [
    customAlert,
    setCustomAlert,
  ] = useState({
    visible: false,
    message: "",
    type: "warning",
  });

  const withdrawalSpeedRef =
    useRef(null);

  /* =======================================================
     ALERT
  ======================================================= */

  const showCustomAlert = (
    message,
    type = "warning"
  ) => {
    setCustomAlert({
      visible: true,
      message,
      type,
    });
  };

  const hideCustomAlert = () => {
    setCustomAlert({
      visible: false,
      message: "",
      type: "warning",
    });
  };

  /* =======================================================
     BACK
  ======================================================= */

  const handleBack = () => {
    if (
      window.history.length > 1
    ) {
      navigate(-1);
      return;
    }

    navigate(
      "/home",
      {
        replace: true,
      }
    );
  };

  /* =======================================================
     WITHDRAWAL OPTION
  ======================================================= */

  const selectedWithdrawalOption =
    useMemo(() => {
      return (
        withdrawalOptions.find(
          (option) =>
            option.id ===
            withdrawalType
        ) || null
      );
    }, [
      withdrawalType,
    ]);

  const withdrawalFee =
    selectedWithdrawalOption
      ? Number(
          selectedWithdrawalOption.fee
        )
      : 0;

  /* =======================================================
     CURRENT BALANCE
  ======================================================= */

  const balance = Number(
    balances[
      selectedCurrency
    ] || 0
  );

  /* =======================================================
     CURRENT COUNTRY
  ======================================================= */

  const selectedCountry =
    currencyMap[
      selectedCurrency
    ] || "";

  /* =======================================================
     WALLET CURRENCIES
  ======================================================= */

  const walletCurrencies =
    useMemo(() => {
      const currencies = [];

      userWallets.forEach(
        (wallet) => {
          const currency =
            String(
              wallet?.currency ||
                ""
            )
              .trim()
              .toUpperCase();

          if (
            currency &&
            !currencies.includes(
              currency
            )
          ) {
            currencies.push(
              currency
            );
          }
        }
      );

      return currencies;
    }, [
      userWallets,
    ]);

  /* =======================================================
     CURRENCY OPTIONS
  ======================================================= */

  const currencyOptions =
    useMemo(() => {
      const positiveBalances =
        Object.keys(
          balances
        ).filter(
          (currency) =>
            Number(
              balances[
                currency
              ] || 0
            ) > 0
        );

      return [
        ...new Set([
          ...positiveBalances,
          ...walletCurrencies,
          "TZS",
        ]),
      ];
    }, [
      balances,
      walletCurrencies,
    ]);

  /* =======================================================
     FILTER WALLETS

     USDT:
     The balance is USDT, but payout wallet can be
     mobile money in the selected country.
  ======================================================= */

  const filteredWallets =
    useMemo(() => {
      return userWallets.filter(
        (wallet) => {
          const walletCurrency =
            String(
              wallet?.currency ||
                ""
            )
              .trim()
              .toUpperCase();

          if (
            selectedCurrency ===
            "USDT"
          ) {
            return true;
          }

          if (
            selectedCurrency ===
            "TZS"
          ) {
            return (
              walletCurrency ===
                "TZS" ||
              String(
                wallet?.country ||
                  ""
              )
                .trim()
                .toLowerCase() ===
                "tanzania"
            );
          }

          return (
            walletCurrency ===
            selectedCurrency
          );
        }
      );
    }, [
      userWallets,
      selectedCurrency,
    ]);

  /* =======================================================
     READY WALLETS
  ======================================================= */

  const readyWallets =
    useMemo(() => {
      return filteredWallets.filter(
        isWalletReady
      );
    }, [
      filteredWallets,
    ]);

  /* =======================================================
     WITHDRAWAL CHANNELS
  ======================================================= */

  const withdrawalChannels =
    useMemo(() => {
      const channels = [];

      readyWallets.forEach(
        (wallet) => {
          /*
            PRIMARY
          */

          const primaryNumber =
            String(
              wallet.phoneNumber1 ||
                wallet.number ||
                ""
            ).trim();

          const primaryNetwork =
            String(
              wallet.network1 ||
                wallet.network ||
                ""
            ).trim();

          if (
            primaryNumber &&
            primaryNetwork
          ) {
            channels.push({
              ...wallet,

              number:
                primaryNumber,

              phoneNumber:
                primaryNumber,

              phoneNumber1:
                primaryNumber,

              network:
                primaryNetwork,

              network1:
                primaryNetwork,

              walletSlot:
                "primary",

              channelId:
                `${wallet.currency}-primary-${primaryNumber}`,
            });
          }

          /*
            SECONDARY
          */

          const secondNumber =
            String(
              wallet.phoneNumber2 ||
                ""
            ).trim();

          const secondNetwork =
            String(
              wallet.network2 ||
                ""
            ).trim();

          if (
            secondNumber &&
            secondNetwork
          ) {
            channels.push({
              ...wallet,

              number:
                secondNumber,

              phoneNumber:
                secondNumber,

              phoneNumber2:
                secondNumber,

              network:
                secondNetwork,

              network2:
                secondNetwork,

              walletSlot:
                "secondary",

              channelId:
                `${wallet.currency}-secondary-${secondNumber}`,
            });
          }
        }
      );

      return channels;
    }, [
      readyWallets,
    ]);

  const hasWallet =
    withdrawalChannels.length >
    0;

  /* =======================================================
     RESET WHEN CURRENCY CHANGES
  ======================================================= */

  useEffect(() => {
    setSelectedWallet("");
    setAmount("");
    setAmountError(false);
  }, [
    selectedCurrency,
  ]);

  /* =======================================================
     BET PROGRESS
  ======================================================= */

  const betProgress =
    Math.min(
      totalBets,
      requiredBets
    );

  const progressPercent =
    Math.min(
      (
        totalBets /
        requiredBets
      ) * 100,
      100
    );

  /* =======================================================
     MINIMUM AMOUNT

     USDT:
       10,000 TZS / 2660
       = approximately 3.759398 USDT
  ======================================================= */

  const minAmount =
    selectedCurrency ===
    "USDT"
      ? MIN_USDT
      : selectedCurrency ===
        "TZS"
      ? DEFAULT_MIN_TZS
      : (
          countryMinAmounts[
            selectedCountry
          ] ||
          DEFAULT_MIN_TZS
        );

  /* =======================================================
     MAXIMUM AMOUNT
  ======================================================= */

  const maxAmount =
    selectedCurrency ===
    "USDT"
      ? MAX_USDT
      : selectedCurrency ===
        "TZS"
      ? DEFAULT_MAX_TZS
      : Math.floor(
          (
            DEFAULT_MAX_TZS /
            DEFAULT_MIN_TZS
          ) *
            minAmount
        );

  /* =======================================================
     FEE
  ======================================================= */

  const feeAmount =
    useMemo(() => {
      const numericAmount =
        Number(
          amount || 0
        );

      if (
        !numericAmount ||
        numericAmount <= 0 ||
        !selectedWithdrawalOption
      ) {
        return 0;
      }

      return (
        numericAmount *
        withdrawalFee
      ) / 100;
    }, [
      amount,
      withdrawalFee,
      selectedWithdrawalOption,
    ]);

  /* =======================================================
     ACTUAL RECEIVE AMOUNT
  ======================================================= */

  const actualAmount =
    useMemo(() => {
      const numericAmount =
        Number(
          amount || 0
        );

      if (
        !numericAmount ||
        numericAmount <= 0
      ) {
        return "0.00";
      }

      const result =
        numericAmount -
        feeAmount;

      return result.toFixed(
        6
      );
    }, [
      amount,
      feeAmount,
    ]);

  /* =======================================================
     FETCH USER BALANCES
  ======================================================= */

  useEffect(() => {
    const fetchBalance =
      async () => {
        if (!username) {
          return;
        }

        try {
          const userQuery =
            query(
              collection(
                db,
                "users"
              ),
              where(
                "username",
                "==",
                username
              )
            );

          const snapshot =
            await getDocs(
              userQuery
            );

          if (
            snapshot.empty
          ) {
            setBalances({});
            return;
          }

          const userData =
            snapshot.docs[0].data();

          const loadedBalances =
            {};

          Object.entries(
            balanceFields
          ).forEach(
            ([
              currency,
              field,
            ]) => {
              loadedBalances[
                currency
              ] =
                Number(
                  userData[
                    field
                  ] || 0
                );
            }
          );

          setBalances(
            loadedBalances
          );

          /*
            Select first positive balance.
          */

          const firstPositive =
            Object.keys(
              loadedBalances
            ).find(
              (currency) =>
                Number(
                  loadedBalances[
                    currency
                  ] || 0
                ) > 0
            );

          if (
            firstPositive
          ) {
            setSelectedCurrency(
              firstPositive
            );
          }
        } catch (error) {
          console.error(
            "Balance loading error:",
            error
          );
        }
      };

    fetchBalance();
  }, [
    username,
  ]);


/* =======================================================
   FETCH USDT FREE BALANCE AFTER FOREX MARGIN
======================================================= */

useEffect(() => {
  let mounted = true;

  const fetchFreeBalance = async () => {
    if (!username) return;

    if (selectedCurrency !== "USDT") {
      setFreeBalance(0);
      return;
    }

    try {
      setMarginLoading(true);

      const result =
        await checkWithdrawableBalance(username);

      if (mounted) {
        setFreeBalance(result.freeBalance);
      }
    } catch (error) {
      console.error(
        "Free balance loading error:",
        error
      );

      if (mounted) {
        setFreeBalance(0);
      }
    } finally {
      if (mounted) {
        setMarginLoading(false);
      }
    }
  };

  fetchFreeBalance();

  return () => {
    mounted = false;
  };
}, [username, selectedCurrency]);


  /* =======================================================
     FETCH DEPOSITS + BET PROGRESS

     First successful deposit cycle: 6 bets.
     If the user already completed the initial 6-bet rule and
     then makes another successful deposit: require 4 new bets
     placed after that latest successful deposit.
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    const toDate = (value) => {
      if (!value) return null;
      if (typeof value.toDate === "function") return value.toDate();
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const fetchBetsAndDepositProgress = async () => {
      if (!username) return;

      try {
        const [antSnapshot, correctSnapshot, transactionSnapshot] =
          await Promise.all([
            getDocs(query(collection(db, "antscore"), where("username", "==", username))),
            getDocs(query(collection(db, "correctscore"), where("username", "==", username))),
            getDocs(query(collection(db, "tsh_transactions"), where("username", "==", username))),
          ]);

        const allBets = [
          ...antSnapshot.docs.map((item) => item.data()),
          ...correctSnapshot.docs.map((item) => item.data()),
        ];

        const successfulDeposits = transactionSnapshot.docs
          .map((item) => item.data())
          .filter((transaction) =>
            String(transaction.type || "").toLowerCase() === "deposit" &&
            String(transaction.status || "").toLowerCase() === "success"
          )
          .map((transaction) => ({
            ...transaction,
            _date: toDate(transaction.createdAt || transaction.timestamp || transaction.date),
          }))
          .filter((transaction) => transaction._date)
          .sort((a, b) => a._date - b._date);

        let betsToCount = allBets;
        let nextRequiredBets = DEFAULT_REQUIRED_BETS;

        if (successfulDeposits.length > 0) {
          const latestDeposit = successfulDeposits[successfulDeposits.length - 1];
          const latestDepositTime = latestDeposit._date.getTime();

          const betsBeforeLatestDeposit = allBets.filter((bet) => {
            const betDate = toDate(bet.createdAt || bet.timestamp || bet.betTime || bet.date);
            return betDate && betDate.getTime() < latestDepositTime;
          }).length;

          // Subsequent deposits after the user has already reached the initial
          // six-bet threshold use a fresh four-bet requirement.
          if (successfulDeposits.length > 1 && betsBeforeLatestDeposit >= DEFAULT_REQUIRED_BETS) {
            nextRequiredBets = REPEAT_DEPOSIT_REQUIRED_BETS;
          }

          // Only bets created after the most recent successful deposit count
          // toward the current deposit's wagering requirement.
          betsToCount = allBets.filter((bet) => {
            const betDate = toDate(bet.createdAt || bet.timestamp || bet.betTime || bet.date);
            return betDate && betDate.getTime() >= latestDepositTime;
          });
        }

        if (mounted) {
          setRequiredBets(nextRequiredBets);
          setTotalBets(betsToCount.length);
        }
      } catch (error) {
        console.error("Bet/deposit progress loading error:", error);
      }
    };

    fetchBetsAndDepositProgress();
    return () => { mounted = false; };
  }, [username]);

  /* =======================================================
     FETCH SECURITY PROFILE
  ======================================================= */

  useEffect(() => {
    const fetchSecurity =
      async () => {
        if (!username) {
          setSecurityLoading(
            false
          );

          navigate(
            "/login",
            {
              replace: true,
            }
          );

          return;
        }

        try {
          const securityRef =
            doc(
              db,
              "security",
              username
            );

          const snapshot =
            await getDoc(
              securityRef
            );

          if (
            !snapshot.exists()
          ) {
            navigate(
              "/profile",
              {
                replace: true,
              }
            );

            return;
          }

          const data =
            snapshot.data();

          const profileComplete =
            Boolean(
              String(
                data.realName ||
                  ""
              ).trim()
            ) &&
            Boolean(
              String(
                data.withdrawPassword ||
                  ""
              ).trim()
            ) &&
            Boolean(
              String(
                data.securityQuestion ||
                  ""
              ).trim()
            ) &&
            Boolean(
              String(
                data.securityAnswer ||
                  ""
              ).trim()
            );

          if (
            !profileComplete
          ) {
            navigate(
              "/profile",
              {
                replace: true,
              }
            );
          }
        } catch (error) {
          console.error(
            "Security loading error:",
            error
          );
        } finally {
          setSecurityLoading(
            false
          );
        }
      };

    fetchSecurity();
  }, [
    username,
    navigate,
  ]);

  /* =======================================================
     FETCH ALL WALLETS
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    const fetchAllWallets =
      async () => {
        if (!username) {
          setUserWallets([]);
          setWalletLoading(
            false
          );
          return;
        }

        try {
          setWalletLoading(
            true
          );

          /*
            Tanzania wallet
          */

          const phoneWalletRef =
            doc(
              db,
              "phoneWallet",
              username
            );

          /*
            Other-country wallets
          */

          const securityRef =
            doc(
              db,
              "security",
              username
            );

          const [
            phoneSnapshot,
            securitySnapshot,
          ] =
            await Promise.all([
              getDoc(
                phoneWalletRef
              ),
              getDoc(
                securityRef
              ),
            ]);

          if (!mounted) {
            return;
          }

          const allWallets =
            [];

          /* ===============================================
             TANZANIA WALLET
          =============================================== */

          if (
            phoneSnapshot.exists()
          ) {
            const wallet =
              phoneSnapshot.data();

            const phone1 =
              String(
                wallet.phoneNumber1 ||
                  ""
              ).trim();

            const phone2 =
              String(
                wallet.phoneNumber2 ||
                  ""
              ).trim();

            const network1 =
              String(
                wallet.network1 ||
                  wallet.network ||
                  ""
              ).trim();

            const network2 =
              String(
                wallet.network2 ||
                  ""
              ).trim();

            const primaryPhoneSecured =
              wallet.primaryPhoneSecured ===
                true ||
              Boolean(phone1);

            const primaryNetworkSelected =
              wallet.primaryNetworkSelected ===
                true ||
              Boolean(network1);

            const secured =
              wallet.walletSecured ===
                true ||
              (
                primaryPhoneSecured &&
                primaryNetworkSelected
              );

            if (
              phone1 &&
              network1 &&
              secured
            ) {
              allWallets.push(
                normalizeWallet({
                  ...wallet,

                  country:
                    "Tanzania",

                  code:
                    "+255",

                  currency:
                    "TZS",

                  network:
                    network1,

                  network1,

                  network2,

                  number:
                    phone1,

                  phoneNumber1:
                    phone1,

                  phoneNumber2:
                    phone2,

                  type:
                    "tanzania-phone-wallet",

                  walletSecured:
                    secured,

                  primaryPhoneSecured:
                    wallet.primaryPhoneSecured ===
                    true,

                  primaryNetworkSelected:
                    wallet.primaryNetworkSelected ===
                    true,

                  secondPhoneAdded:
                    wallet.secondPhoneAdded ===
                    true,

                  secondNetworkSelected:
                    wallet.secondNetworkSelected ===
                    true,
                })
              );
            }
          }

          /* ===============================================
             OTHER COUNTRY WALLETS
          =============================================== */

          if (
            securitySnapshot.exists()
          ) {
            const securityData =
              securitySnapshot.data();

            const savedWallets =
              Array.isArray(
                securityData.wallets
              )
                ? securityData.wallets
                : [];

            savedWallets.forEach(
              (wallet) => {
                const normalized =
                  normalizeWallet(
                    wallet
                  );

                if (
                  !normalized.number ||
                  !normalized.network ||
                  !normalized.currency
                ) {
                  return;
                }

                if (
                  normalized.walletSecured !==
                  true
                ) {
                  return;
                }

                allWallets.push(
                  normalized
                );
              }
            );
          }

          /* ===============================================
             REMOVE DUPLICATES
          =============================================== */

          const uniqueWallets =
            [];

          const seen =
            new Set();

          allWallets.forEach(
            (wallet) => {
              const key = [
                wallet.country,
                wallet.currency,
                wallet.network1 ||
                  wallet.network,
                wallet.number,
                wallet.phoneNumber2,
                wallet.network2,
              ].join("|");

              if (
                !seen.has(key)
              ) {
                seen.add(key);

                uniqueWallets.push(
                  wallet
                );
              }
            }
          );

          setUserWallets(
            uniqueWallets
          );
        } catch (error) {
          console.error(
            "Wallet loading error:",
            error
          );

          if (mounted) {
            setUserWallets([]);
          }
        } finally {
          if (mounted) {
            setWalletLoading(
              false
            );
          }
        }
      };

    fetchAllWallets();

    return () => {
      mounted = false;
    };
  }, [
    username,
  ]);

  /* =======================================================
     AMOUNT CHANGE
  ======================================================= */

  const handleAmountChange =
    (event) => {
      const value =
        event.target.value;

      if (
        value === ""
      ) {
        setAmount("");
        setAmountError(false);
        return;
      }

      const numericValue =
        Number(value);

      if (
        Number.isNaN(
          numericValue
        )
      ) {
        return;
      }

      /*
        Do not allow amount above balance.
      */

        const availableBalance =
        selectedCurrency === "USDT"
          ? freeBalance
          : balance;
      
      if (numericValue > availableBalance) {
        setAmount(String(availableBalance));
      
        setAmountError(
          availableBalance < Number(minAmount)
        );
      
        return;
      }
        
      setAmount(value);

      setAmountError(
        numericValue <
          Number(minAmount) ||
        numericValue >
          Number(maxAmount)
      );
    };

  /* =======================================================
     ADD WALLET
  ======================================================= */

  const handleAddWallet =
    () => {
      if (
        selectedCurrency ===
        "TZS"
      ) {
        navigate(
          "/add-card"
        );

        return;
      }

      navigate(
        "/East-Wallet"
      );
    };

  /* =======================================================
     WITHDRAWAL SPEED ERROR
  ======================================================= */

  const showWithdrawalSpeedError =
    () => {
      setWithdrawalSpeedError(
        true
      );

      setTimeout(() => {
        withdrawalSpeedRef.current?.scrollIntoView(
          {
            behavior:
              "smooth",
            block:
              "center",
          }
        );
      }, 50);
    };

  /* =======================================================
     SUBMIT WITHDRAWAL
  ======================================================= */

  const handleSubmit =
    async () => {
      /* ===============================================
         LOGIN
      =============================================== */

      if (!username) {
        showCustomAlert(
          t("User session not found. Please login again.")
        );

        navigate(
          "/login"
        );

        return;
      }

      /* ===============================================
         WITHDRAWAL TIME
      =============================================== */

      if (
        !withdrawalType
      ) {
        showWithdrawalSpeedError();
        return;
      }

      /* ===============================================
         SECURITY
      =============================================== */

      if (
        securityLoading
      ) {
        showCustomAlert(
          t("Checking profile information...")
        );

        return;
      }

      try {
        const securityRef =
          doc(
            db,
            "security",
            username
          );

        const securitySnapshot =
          await getDoc(
            securityRef
          );

        if (
          !securitySnapshot.exists()
        ) {
          showCustomAlert(
            t("Please complete your profile first.")
          );

          navigate(
            "/profile"
          );

          return;
        }

        const data =
          securitySnapshot.data();

        const profileComplete =
          Boolean(
            String(
              data.realName ||
                ""
            ).trim()
          ) &&
          Boolean(
            String(
              data.withdrawPassword ||
                ""
            ).trim()
          ) &&
          Boolean(
            String(
              data.securityQuestion ||
                ""
            ).trim()
          ) &&
          Boolean(
            String(
              data.securityAnswer ||
                ""
            ).trim()
          );

        if (
          !profileComplete
        ) {
          showCustomAlert(
            t("Complete your security profile first ⚠️")
          );

          navigate(
            "/profile"
          );

          return;
        }
      } catch (error) {
        console.error(
          "Profile validation error:",
          error
        );

        showCustomAlert(
          t("Unable to verify profile information.")
        );

        return;
      }

      /* ===============================================
         WALLET LOADING
      =============================================== */

      if (
        walletLoading
      ) {
        showCustomAlert(
          t("Loading wallet...")
        );

        return;
      }

      /* ===============================================
         BET REQUIREMENT
      =============================================== */

      if (
        totalBets <
        requiredBets
      ) {
        const remaining =
          requiredBets -
          totalBets;

        showCustomAlert(
          `You must complete ${requiredBets} valid bets first. Remaining: ${remaining} bet${
            remaining === 1
              ? ""
              : "s"
          }.`
        );

        return;
      }

      /* ===============================================
         AMOUNT
      =============================================== */

      const numericAmount =
        Number(amount);

      if (
        !numericAmount ||
        numericAmount <= 0
      ) {
        showCustomAlert(
          t("Please enter withdrawal amount.")
        );

        return;
      }

      /* ===============================================
         MINIMUM
      =============================================== */

      if (
        numericAmount <
        minAmount
      ) {
        setAmountError(
          true
        );

        showCustomAlert(
          `Minimum amount is ${formatAmount(
            minAmount
          )} ${selectedCurrency}`
        );

        return;
      }

      /* ===============================================
         MAXIMUM
      =============================================== */

      if (
        numericAmount >
        maxAmount
      ) {
        setAmountError(
          true
        );

        showCustomAlert(
          `Maximum amount is ${formatAmount(
            maxAmount
          )} ${selectedCurrency}`
        );

        return;
      }

      /* ===============================================
         BALANCE
      =============================================== */

     /* ===============================================
   BALANCE + FOREX MARGIN CHECK
=============================================== */

if (selectedCurrency === "USDT") {
  try {
    const { freeBalance } =
      await checkWithdrawableBalance(username);

    if (numericAmount > freeBalance) {
      showCustomAlert(
        `Insufficient withdrawable USDT. Available after Forex margin: ${freeBalance.toFixed(2)} USDT.`
      );
      return;
    }
  } catch (error) {
    console.error("Withdrawable balance check failed:", error);

    showCustomAlert(
      error.message || t("Unable to verify withdrawable balance.")
    );
    return;
  }
} else if (numericAmount > balance) {
  showCustomAlert(t("Insufficient balance ❌"));
  return;
}

      /* ===============================================
         WALLET
      =============================================== */

      if (
        !hasWallet
      ) {
        showCustomAlert(
          `No secured wallet found for ${selectedCurrency}.`
        );

        return;
      }

      /* ===============================================
         SELECT WALLET
      =============================================== */

      if (
        selectedWallet ===
        ""
      ) {
        showCustomAlert(
          t("Please select wallet.")
        );

        return;
      }

      const walletIndex =
        Number(
          selectedWallet
        );

      const selectedChannel =
        withdrawalChannels[
          walletIndex
        ];

      if (
        !selectedChannel
      ) {
        showCustomAlert(
          t("Selected wallet not found.")
        );

        return;
      }

      /* ===============================================
         FINAL WALLET SECURITY
      =============================================== */

      if (
        !isWalletReady(
          selectedChannel
        )
      ) {
        showCustomAlert(
          t("Selected wallet is not fully secured.")
        );

        return;
      }

      /* ===============================================
         PHONE / NETWORK
      =============================================== */

      const selectedPhoneNumber =
        String(
          selectedChannel.phoneNumber ||
            selectedChannel.number ||
            ""
        ).trim();

      const selectedNetwork =
        String(
          selectedChannel.network ||
            ""
        ).trim();

      if (
        !selectedPhoneNumber ||
        !selectedNetwork
      ) {
        showCustomAlert(
          t("Selected payment channel is incomplete.")
        );

        return;
      }

      /* ===============================================
         FINAL WALLET OBJECT
      =============================================== */

      const wallet = {
        ...selectedChannel,

        number:
          selectedPhoneNumber,

        phoneNumber:
          selectedPhoneNumber,

        selectedPhoneNumber:
          selectedPhoneNumber,

        network:
          selectedNetwork,

        selectedNetwork:
          selectedNetwork,

        selectedWalletType:
          selectedChannel.walletSlot,

        walletSlot:
          selectedChannel.walletSlot,
      };

      /* ===============================================
         FEE
      =============================================== */

      const selectedFee =
        Number(
          selectedWithdrawalOption?.fee ||
            0
        );

      const calculatedFee =
        (
          numericAmount *
          selectedFee
        ) / 100;

      const receiveAmount =
        numericAmount -
        calculatedFee;

      if (
        receiveAmount <= 0
      ) {
        showCustomAlert(
          t("Withdrawal amount is too small after fees.")
        );

        return;
      }

      /* ===============================================
         EXCHANGE RATE
      =============================================== */

      const exchangeRate =
        getExchangeRate(
          selectedCurrency,
          wallet
        );

      /*
        If user withdraws USDT:

          amount = USDT

          localEquivalent =
            USDT * local rate

        Example Tanzania:

          100 USDT * 2660
          = 266,000 TZS
      */

      let usdtAmount;
      let localEquivalent;
      let payoutCurrency;

      if (
        selectedCurrency ===
        "USDT"
      ) {
        usdtAmount =
          receiveAmount;

        localEquivalent =
          receiveAmount *
          exchangeRate;

        payoutCurrency =
          wallet.currency ||
          "TZS";
      } else {
        usdtAmount =
          receiveAmount /
          exchangeRate;

        localEquivalent =
          receiveAmount;

        payoutCurrency =
          selectedCurrency;
      }

      /* ===============================================
         NAVIGATE TO CONFIRMATION
      =============================================== */

      navigate(
        "/Confirmed-Withdraw",
        {
          state: {
            /*
              Original withdrawal amount.
            */
            amount:
              numericAmount,

            /*
              Currency user selected.
            */
            currency:
              selectedCurrency,

            sourceCurrency:
              selectedCurrency,

            targetCurrency:
              selectedCurrency,

            receiveCurrency:
              selectedCurrency,

            /*
              Amount after fee.
            */
            actualAmount:
              receiveAmount.toFixed(
                6
              ),

            /*
              IMPORTANT:
              Always send USDT amount separately.
            */
            usdtAmount:
              Number(
                usdtAmount.toFixed(
                  6
                )
              ),

            /*
              Local equivalent.
            */
            localEquivalent:
              Number(
                localEquivalent.toFixed(
                  2
                )
              ),

            /*
              1 USDT = exchangeRate local currency.
            */
            exchangeRate,

            /*
              Payout currency.
            */
            payoutCurrency,

            /*
              For Tanzania this is TZS.
              For Kenya KES, etc.
            */
            payoutRate:
              exchangeRate,

            /*
              Selected wallet.
            */
            wallet,

            /*
              Withdrawal type.
            */
            withdrawalType:
              selectedWithdrawalOption.id,

            processingTime:
              selectedWithdrawalOption.time,

            /*
              Fee percentage.
            */
            fee:
              selectedFee,

            /*
              Actual fee.
            */
            feeAmount:
              calculatedFee,

            /*
              Explicit payment channel.
            */
            selectedPhoneNumber,

            selectedNetwork,

            selectedWalletType:
              selectedChannel.walletSlot,

            walletSlot:
              selectedChannel.walletSlot,

            /*
              User.
            */
            username,

            /*
              Explicit balance field.
            */
            balanceField:
              balanceFields[
                selectedCurrency
              ],

            /*
              USDT must always use
              usdtBalanceOther.
            */
            sourceBalanceField:
              selectedCurrency ===
              "USDT"
                ? "usdtBalanceOther"
                : balanceFields[
                    selectedCurrency
                  ],

            /*
              Helps Confirmed-Withdraw
              know the denomination.
            */
            isUSDT:
              selectedCurrency ===
              "USDT",

            /*
              Tanzania reference.
            */
            tzsPerUSDT:
              TZS_PER_USDT,
          },
        }
      );
    };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="local-withdraw-page">

      {/* ===================================================
          CUSTOM ALERT
      =================================================== */}

      {customAlert.visible && (
        <div
          className="withdraw-custom-alert-overlay"
          onClick={
            hideCustomAlert
          }
        >
          <div
            className={`withdraw-custom-alert withdraw-alert-${customAlert.type}`}
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="withdraw-alert-icon">
              {customAlert.type ===
              "success"
                ? "✓"
                : "!"}
            </div>

            <div className="withdraw-alert-content">
              <strong>
                {t("Notice")}
              </strong>

              <span>
                {
                  customAlert.message
                }
              </span>
            </div>

            <button
              type="button"
              className="withdraw-alert-close"
              onClick={
                hideCustomAlert
              }
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* ===================================================
          HEADER
      =================================================== */}

      <div className="header withdraw-header">
        <button
          type="button"
          className="withdraw-back-btn"
          onClick={
            handleBack
          }
          aria-label="Go back"
        >
          ‹
        </button>
      </div>

      {/* ===================================================
          TOP BUTTONS
      =================================================== */}

      <div className="top-buttons">

        <button
          type="button"
          className="active-btn"
        >
          {t("Withdraw Channel mobile_money")}
        </button>

        <button
          type="button"
          onClick={() =>
            navigate(
              "/withdraw-by-usdt"
            )
          }
        >
          {t("USDT / Crypto Management")}
        </button>

      </div>

      {/* ===================================================
          BET PROGRESS
      =================================================== */}

      <div className="section trade-section">

        <div className="row">

          <span>
            {t("Plan Progress")}
          </span>

          <b className="green">
            {betProgress}
            {" / "}
            {requiredBets}
          </b>

        </div>

        <div className="progress-bar">

          <div
            className="progress-fill"
            style={{
              width:
                `${progressPercent}%`,
            }}
          />

        </div>

      </div>

      {/* ===================================================
          BALANCE CURRENCY
      =================================================== */}

      <div className="section compact-section">

        <div className="row column">

          <span>
            {t("Balance Currency")}
          </span>

          <select
            value={
              selectedCurrency
            }
            onChange={(event) =>
              setSelectedCurrency(
                event.target.value
              )
            }
          >

            {currencyOptions.map(
              (currency) => (
                <option
                  key={
                    currency
                  }
                  value={
                    currency
                  }
                >
                  {currency}
                  {" - "}
                  {formatAmount(
                    balances[
                      currency
                    ] || 0
                  )}
                </option>
              )
            )}

          </select>

        </div>

      </div>

      {/* ===================================================
          USDT RATE INFORMATION
      =================================================== */}

      {selectedCurrency ===
        "USDT" && (
        <div className="section compact-section">

          <div className="row">
            <span>
              {t("USDT Rate")}
            </span>

            <b>
              1 USDT =
              {" "}
              {formatAmount(
                getExchangeRate(
                  "USDT",
                  withdrawalChannels[
                    Number(
                      selectedWallet
                    )
                  ]
                )
              )}
              {" "}
              {
                withdrawalChannels[
                  Number(
                    selectedWallet
                  )
                ]?.currency ||
                "TZS"
              }
            </b>
          </div>

          
<div className="row">
  <span>USDT Balance</span>

  <b>
    {formatAmount(balance)} USDT
  </b>
</div>

<div className="row">
  <span>Free Balance</span>

  <b className="green">
    {marginLoading
      ? "Calculating..."
      : `${formatAmount(freeBalance)} USDT`}
  </b>
</div>

        </div>
      )}

      {/* ===================================================
          PAYMENT CHANNEL
      =================================================== */}

      <div className="section compact-section">

        <div className="row column">

          <span>
            {t("Payment Channel")}
          </span>

          {walletLoading ? (

            <div className="wallet-loading">
              {t(t("Loading wallet..."))}
            </div>

          ) : hasWallet ? (

            <select
              value={
                selectedWallet
              }
              onChange={(event) =>
                setSelectedWallet(
                  event.target.value
                )
              }
            >

              <option value="">
                {t("Select Wallet")}
              </option>

              {withdrawalChannels.map(
                (
                  channel,
                  index
                ) => (
                  <option
                    key={
                      channel.channelId ||
                      index
                    }
                    value={
                      index
                    }
                  >
                    {
                      channel.network ||
                      "Mobile Wallet"
                    }
                    {" ("}
                    {maskNumber(
                      channel.phoneNumber
                    )}
                    {")"}
                  </option>
                )
              )}

            </select>

          ) : (

            <div className="wallet-box">

              <b className="red-text">
                {t("No wallet found for")}{" "}
                {
                  selectedCurrency
                }
              </b>

              <button
                type="button"
                className="add-wallet-btn"
                onClick={
                  handleAddWallet
                }
              >
                {t("Add Wallet")}
              </button>

            </div>

          )}

        </div>

      </div>

      {/* ===================================================
          WITHDRAWAL SPEED
      =================================================== */}

      <div
        ref={
          withdrawalSpeedRef
        }
        className={
          withdrawalSpeedError
            ? "section compact-section withdrawal-speed-error"
            : "section compact-section"
        }
      >

        <div className="row column">

          <span>
            {t("Withdrawal Speed")}
          </span>

          <div className="withdraw-speed-select-wrapper">

            <select
              value={
                withdrawalType
              }
              onChange={(event) => {

                setWithdrawalType(
                  event.target.value
                );

                setWithdrawalSpeedError(
                  false
                );

              }}
              aria-invalid={
                withdrawalSpeedError
              }
            >

              <option value="">
                {t("Select Withdrawal Time")}
              </option>

              {withdrawalOptions.map(
                (option) => (
                  <option
                    key={
                      option.id
                    }
                    value={
                      option.id
                    }
                  >
                    {t(option.label)}
                    {" - "}
                    {t(option.time)}
                    {" - "}
                    {option.fee}%
                    {" " + t("Fee")}
                  </option>
                )
              )}

            </select>

          </div>

          {withdrawalSpeedError && (
            <div className="withdrawal-speed-error-message">

              <span className="withdraw-error-dot">
                !
              </span>

              <span>
                {t("Please select withdrawal time")}
              </span>

            </div>
          )}

        </div>

      </div>

      {/* ===================================================
          ENTER AMOUNT
      =================================================== */}

      <div className="section compact-section amount-section">

        <p className="placeholder">
          {t("Please Enter The Amount")}
        </p>

        <input
          type="number"
          min="0"
          step={
            selectedCurrency ===
            "USDT"
              ? "0.000001"
              : "0.01"
          }
          value={
            amount
          }
          onChange={
            handleAmountChange
          }
          placeholder={
            `${t("Enter amount Min")} ${formatAmount(
              minAmount
            )} - Max ${formatAmount(
              maxAmount
            )}`
          }
        />

      </div>

      {/* ===================================================
          WITHDRAWAL SUMMARY
      =================================================== */}

      <div className="section withdrawal-summary">

        <div className="row">

          <span>
            {t("Withdrawal Speed")}
          </span>

          <b>
            {
              selectedWithdrawalOption
                ? selectedWithdrawalOption.time
                : "-"
            }
          </b>

        </div>

        <div className="row">

          <span>
            {t("Withdrawal Fee")}
          </span>

          <b>
            {
              selectedWithdrawalOption
                ? `${withdrawalFee}%`
                : "-"
            }
          </b>

        </div>

        <div className="row">

          <span>
            {t("Fee Amount")}
          </span>

          <b>
            {selectedCurrency}
            {" "}
            {formatAmount(
              feeAmount
            )}
          </b>

        </div>

        <div className="row">

          <span>
            {t("You Receive")}
          </span>

          <b>
            {selectedCurrency}
            {" "}
            {actualAmount}
          </b>

        </div>

        {selectedCurrency ===
          "USDT" &&
          amount && (
            <div className="row">

              <span>
                {t("TZS Equivalent")}
              </span>

              <b>
                {formatAmount(
                  Number(
                    actualAmount
                  ) *
                    getExchangeRate(
                      "USDT",
                      withdrawalChannels[
                        Number(
                          selectedWallet
                        )
                      ]
                    )
                )}
              </b>

            </div>
          )}

        {amountError && (
          <div className="row red amount-range-error">

            <span>
              {t("Amount Range")}
            </span>

            <b>
              {formatAmount(
                minAmount
              )}
              {" - "}
              {formatAmount(
                maxAmount
              )}
              {" "}
              {selectedCurrency}
            </b>

          </div>
        )}

      </div>

      {/* ===================================================
          FEE NOTE
      =================================================== */}

      <div className="note">

        <span>
          {
            selectedWithdrawalOption
              ? `${t(selectedWithdrawalOption.time)} • ${withdrawalFee}% ${t("withdrawal fee")}`
              : t("Select withdrawal time to view processing fee")
          }
        </span>

        <br />

        <small>
          {t("Actual transaction amount may still be subject to bank charges.")}
        </small>

      </div>

      {/* ===================================================
          SUBMIT
      =================================================== */}

      <button
        type="button"
        className="submit-fixed"
        onClick={
          handleSubmit
        }
      >
        {t("SUBMIT")}
      </button>

    </div>
  );
}